/**
 * Learner pages that can have guided tours.
 * Targets are NOT listed here — admins enter target keys that match `data-tour-id` on the UI.
 * See wiredTourTargets.ts for keys already wired in components (suggestions only).
 *
 * Simple and Advanced share one tour when the layout is the same.
 * When the layout changes (tile home vs long dashboard, settings list vs tabs),
 * each look has its own page key. The screen chooses the key; the tour player does not.
 */

import { getWiredTourTarget } from './wiredTourTargets';
import type { LearnerHomeDesign } from '../utils/learnerHomeDesign';

/** Page keys that have a separate Simple-look tour. Advanced keeps the base key. */
const SIMPLE_TOUR_PAGE_KEYS: Record<string, string> = {
  dashboard: 'dashboard-simple',
  'quick-settings': 'quick-settings-simple',
};

/** Tour to play for this screen in the current learner look. */
export function tourPageKeyForDesign(basePageKey: string, design: LearnerHomeDesign): string {
  if (design !== 'mme') return basePageKey;
  return SIMPLE_TOUR_PAGE_KEYS[basePageKey] ?? basePageKey;
}

/** Advanced quick settings: one tour per open tab. Simple keeps the menu tour. */
export function quickSettingsTourPageKey(design: LearnerHomeDesign, tabId: string): string {
  if (design === 'mme') return 'quick-settings-simple';
  const slug = tabId === 'lessonText' ? 'lesson-text' : tabId;
  return `quick-settings-${slug}`;
}

/** My Profile in Advanced: one tour per open tab. Simple uses the single `profile` menu tour. */
export function profileTourPageKey(tabId: string): string {
  return `profile-${tabId}`;
}

/** Tab tours start from Help only, so switching tabs does not auto-play the next guide. */
export function isManualOnlyLearnerTourPageKey(pageKey: string): boolean {
  if (pageKey.startsWith('profile-')) return true;
  return pageKey.startsWith('quick-settings-') && pageKey !== 'quick-settings-simple';
}

export interface LearnerTourPageDefinition {
  pageKey: string;
  labelEn: string;
  labelFa: string;
  /** Maps App currentView or composite sub-view key */
  viewKeys: string[];
}

export const LEARNER_TOUR_PAGES: LearnerTourPageDefinition[] = [
  { pageKey: 'dashboard', labelEn: 'Dashboard — advanced', labelFa: 'داشبورد — پیشرفته', viewKeys: ['dashboard'] },
  { pageKey: 'dashboard-simple', labelEn: 'Dashboard — simple', labelFa: 'داشبورد — ساده', viewKeys: [] },
  { pageKey: 'courses-catalog', labelEn: 'Courses — catalog', labelFa: 'دوره‌ها — فهرست', viewKeys: ['courses-catalog'] },
  { pageKey: 'courses-detail', labelEn: 'Courses — course detail', labelFa: 'دوره‌ها — جزئیات دوره', viewKeys: ['courses-detail'] },
  { pageKey: 'library', labelEn: 'Library', labelFa: 'کتابخانه', viewKeys: ['library'] },
  { pageKey: 'book', labelEn: 'Book viewer', labelFa: 'نمایش کتاب', viewKeys: ['book'] },
  { pageKey: 'lesson', labelEn: 'Lesson viewer', labelFa: 'نمایش درس', viewKeys: ['lesson'] },
  { pageKey: 'profile', labelEn: 'Profile', labelFa: 'پروفایل', viewKeys: ['profile'] },
  { pageKey: 'profile-profile', labelEn: 'Profile — account tab', labelFa: 'پروفایل — زبانه حساب', viewKeys: [] },
  { pageKey: 'profile-subscription', labelEn: 'Profile — subscription tab', labelFa: 'پروفایل — زبانه اشتراک', viewKeys: [] },
  { pageKey: 'profile-messages', labelEn: 'Profile — messages tab', labelFa: 'پروفایل — زبانه پیام‌ها', viewKeys: [] },
  { pageKey: 'profile-favorites', labelEn: 'Profile — favorites tab', labelFa: 'پروفایل — زبانه علاقه‌مندی‌ها', viewKeys: [] },
  { pageKey: 'profile-comments', labelEn: 'Profile — comments tab', labelFa: 'پروفایل — زبانه نظرات', viewKeys: [] },
  { pageKey: 'profile-goals', labelEn: 'Profile — goals tab', labelFa: 'پروفایل — زبانه اهداف', viewKeys: [] },
  { pageKey: 'profile-progress', labelEn: 'Profile — progress tab', labelFa: 'پروفایل — زبانه پیشرفت', viewKeys: [] },
  { pageKey: 'tag-content', labelEn: 'Tag content', labelFa: 'محتوای برچسب', viewKeys: ['tag-content'] },
  { pageKey: 'quiz', labelEn: 'Take quiz', labelFa: 'شرکت در آزمون', viewKeys: ['quiz'] },
  { pageKey: 'knowledge-placement', labelEn: 'Knowledge placement', labelFa: 'تعیین سطح دانش', viewKeys: ['knowledge-placement'] },
  { pageKey: 'exercise-flow', labelEn: 'Take exercise', labelFa: 'انجام تمرین', viewKeys: ['exercise-flow'] },
  { pageKey: 'quick-settings', labelEn: 'Quick settings — advanced', labelFa: 'تنظیمات سریع — پیشرفته', viewKeys: [] },
  { pageKey: 'quick-settings-security', labelEn: 'Quick settings — security', labelFa: 'تنظیمات سریع — امنیت', viewKeys: [] },
  { pageKey: 'quick-settings-notifications', labelEn: 'Quick settings — notifications', labelFa: 'تنظیمات سریع — اعلان‌ها', viewKeys: [] },
  { pageKey: 'quick-settings-lesson-text', labelEn: 'Quick settings — lesson text', labelFa: 'تنظیمات سریع — متن درس', viewKeys: [] },
  { pageKey: 'quick-settings-region', labelEn: 'Quick settings — language and time', labelFa: 'تنظیمات سریع — زبان و زمان', viewKeys: [] },
  { pageKey: 'quick-settings-template', labelEn: 'Quick settings — preferences', labelFa: 'تنظیمات سریع — ترجیحات', viewKeys: [] },
  { pageKey: 'quick-settings-simple', labelEn: 'Quick settings — simple', labelFa: 'تنظیمات سریع — ساده', viewKeys: [] },
  { pageKey: 'search', labelEn: 'Search', labelFa: 'جستجو', viewKeys: [] },
  { pageKey: 'leitner', labelEn: 'Leitner box', labelFa: 'جعبه لایتنر', viewKeys: [] },
];

export function getLearnerTourPage(pageKey: string): LearnerTourPageDefinition | undefined {
  return LEARNER_TOUR_PAGES.find((p) => p.pageKey === pageKey);
}

/** Resolves DOM selector for a tour target key (matches data-tour-id on learner UI). */
export function resolveTourTargetSelector(pageKey: string, targetKey: string): string {
  const wired = getWiredTourTarget(pageKey, targetKey);
  if (wired?.selector) return wired.selector;
  const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(targetKey) : targetKey.replace(/"/g, '\\"');
  return `[data-tour-id="${escaped}"]`;
}

export function resolveLearnerTourPageKey(view: string, subView?: string | null): string | null {
  if (subView) {
    const composite = `${view}-${subView}`;
    const byComposite = LEARNER_TOUR_PAGES.find((p) => p.viewKeys.includes(composite));
    if (byComposite) return byComposite.pageKey;
  }
  const direct = LEARNER_TOUR_PAGES.find((p) => p.viewKeys.includes(view));
  return direct?.pageKey ?? null;
}

export const LEARNER_TOUR_SEEN_STORAGE_PREFIX = 'persianist_tour_seen_v1_';
export const LEARNER_TOUR_GLOBAL_DISABLED_KEY = 'persianist_tour_disabled_all_v1';

export function hasSeenTour(pageKey: string): boolean {
  try {
    return localStorage.getItem(`${LEARNER_TOUR_SEEN_STORAGE_PREFIX}${pageKey}`) === '1';
  } catch {
    return false;
  }
}

export function markTourSeen(pageKey: string): void {
  try {
    localStorage.setItem(`${LEARNER_TOUR_SEEN_STORAGE_PREFIX}${pageKey}`, '1');
  } catch {
    /* ignore */
  }
}

export function isTourGloballyDisabled(): boolean {
  try {
    return localStorage.getItem(LEARNER_TOUR_GLOBAL_DISABLED_KEY) === '1';
  } catch {
    return false;
  }
}

export function setTourGloballyDisabled(): void {
  try {
    localStorage.setItem(LEARNER_TOUR_GLOBAL_DISABLED_KEY, '1');
  } catch {
    /* ignore */
  }
}
